import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { claimCommittee, claimBasisLabel, claimFiledAtSeconds } from '../data/claimPayload';
import { collection, onSnapshot, query, doc, getDoc, where } from 'firebase/firestore';
import { LADDER, ELEVATION_STAGE } from '../data/claimLadder';   // R-6b: one ladder, not four copies of it
import { subscribeVisibleClaims } from '../data/claimQueries';
import { subscribeVisibleCases } from '../data/disciplineQueries';   // T5 / D-18: forwarded discipline cases, counted for the GC
import { forwardedDisciplineToGC } from '../data/gcDesk';   // T5 / D-18: the selection lives there, not here (no hand-kept status)
import { needsYouNow, workCounts } from '../data/dashboardFocus';   // the front page's one question (redesign of 2026-10-24)
import ClaimTally from '../components/ClaimTally';   // section 493: the read-only claim tally
import NotificationCentre from '../components/NotificationCentre';   // 6a-10 (section 227): the records ruling 13 and 89 write, finally read
import { HandoverInbox } from '../components/HandoverPanel';   // WO-001 S6 (section 243): a handover proposed to me, or by me
import RightsWelcomeCard from '../components/RightsWelcomeCard';   // ruling 306 item 2
import ContactSection from '../components/ContactSection';   // Record 610 (ruling 259): the Contact section of the profile
import OfficeCopyPanel from '../components/OfficeCopyPanel';   // WO-001 A3.2 (section 246): my marked copy as an outgoing officer

// Statuses that are NOT open work: retired, or not yet filed.
const NOT_OPEN = ['closure', 'archived', 'draft'];

const ELEVATION_LABEL = (LADDER.find(s => s.key === ELEVATION_STAGE) || {}).label || 'GC Review';

// R-6b, 2026-09-21: THIS WAS THE THIRD HAND-KEPT COPY OF THE LADDER, and like the other two it was
// missing `gc_review`. A claim a Local Chairperson had sent up therefore appeared in NO pipeline
// stage here at all - it did not show as waiting, it did not show anywhere. It simply left the row,
// on the screen an officer opens first.
//
// Derived from src\data\claimLadder.js now, so a rung added there appears here by itself
// (directives section XI). The labels are the ladder's own, which is also why the stage bar, the
// Claims filter row and this pipeline finally agree on what to call Doc Review.
const PIPELINE_STAGES = LADDER.map(s => ({ label: s.label, key: s.key }));

// THE FRONT PAGE REDESIGN (Bob, 2026-10-24: "that whole entire front page is bullshit and overwhelms
// the user, figure out a better opening presentation"; spec: data\YCP_FRONT_PAGE_REDESIGN_2026-10-24.md).
//
// Before today this screen rendered SEVEN stacked panels of equal weight - a notification feed, a
// handover inbox, an outgoing officer's copy, a claim tally, a rights welcome card, a contact block,
// then six stat cards, a nine-rung pipeline and a table - and nothing on it said what to look at
// first. Three zones now, in strict priority:
//
//   ZONE 1  ACT    "Needs you now" - one ranked list, each row one click. src\data\dashboardFocus.js.
//   ZONE 2  WORK   one quiet strip of counts that are doors.
//   ZONE 3  MORE   every panel above, collapsed behind one button. NOTHING WAS DELETED.
//
// What was removed from the landing is only DUPLICATION: the draft-expiry banners and the urgent
// alert banner, both of which are now rows in zone 1, and the return-visitor strip, whose only
// action (Quick File) is the button in the top bar beside it. The pipeline moved into More.
const SEV = {
  overdue: { colour: '#FF6B6B', border: '#C0392B', back: '#1A0A0A', mark: '⚠' },
  due:     { colour: '#E8A020', border: '#8A5A12', back: '#1A1206', mark: '⏱' },
  urgent:  { colour: '#E8A020', border: '#8A5A12', back: '#1A1206', mark: '⚠' },
  waiting: { colour: '#A8C4DF', border: '#2E4A6B', back: '#0D1B2A', mark: '→' },
};

export default function Dashboard({ user, onNavigate, onViewClaim }) {
  const [claims, setClaims] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showReturnBanner, setShowReturnBanner] = useState(false);
  // Zone 3 is closed when the screen opens. Reference is not the opening.
  const [showMore, setShowMore] = useState(false);

  // T5 / D-18: the General Chairperson's count of discipline cases forwarded to him (gcDesk.forwardedDisciplineToGC). Only the GC
  // (or admin) subscribes; a refusal shows as unknown ('?'), never as zero.
  const [gcCases, setGcCases] = useState(null);
  useEffect(() => {
    if (!(user && (user.role === 'general_chair' || user.isAdmin))) return undefined;
    return subscribeVisibleCases(db, user, onSnapshot, rows => setGcCases(forwardedDisciplineToGC(rows).length), () => setGcCases(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.uid, user && user.role, user && user.committee]);

  useEffect(() => {
    if (!user?.uid) return;
    // Load member's own drafts - single where clause to avoid composite index
    const draftQuery = query(
      collection(db, 'claims'),
      where('createdBy', '==', user.uid)
    );
    const unsubDrafts = onSnapshot(draftQuery, snap => {
      const now = Date.now();
      setDrafts(snap.docs
        .filter(d => d.data().status === 'draft')
        .map(d => ({
          id: d.id,
          ...d.data(),
          daysLeft: d.data().expiresAt
            ? Math.ceil((new Date(d.data().expiresAt).getTime() - now) / (1000 * 60 * 60 * 24))
            : 30,
        })));
    });
    return unsubDrafts;
  }, [user?.uid]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.uid) return;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setProfile(data);
          // Show return visitor banner if they have a railroad on file
          if (data.railroad) setShowReturnBanner(true);
        }
      } catch (e) {
        console.error('Profile load error:', e);
      }
    };
    loadProfile();
  }, [user?.uid]);
  const [loading, setLoading] = useState(true);
  // A1: null means the read answered. A string means it did NOT, and carries why.
  const [readFailed, setReadFailed] = useState(null);

  useEffect(() => {
    // F-15 (SS201): shaped in src\data\claimQueries.js - the read rule excludes drafts, so the
    // query says `status in <non-draft>` beside the scope; an unshaped query is refused outright.
    // The error path used to swallow the refusal and show an empty dashboard as if it were true.
    setReadFailed(null);   // a new subscription has not failed yet
    const unsubscribe = subscribeVisibleClaims(db, user, rows => {
      rows.sort((a, b) => claimFiledAtSeconds(b) - claimFiledAtSeconds(a));
      setClaims(rows);
      setLoading(false);
    }, err => {
      // A1 (audit 2026-09-22). This used to log and set loading false, leaving `claims` at [] -
      // so a REFUSED READ rendered as `0 claims, $0`, identical to a member who has filed nothing.
      // The comment above says the error path "used to swallow the refusal"; it still did. The
      // first screen anyone opens told a man he had nothing when the platform had simply not
      // answered. An empty result and an unanswered question are different facts and this screen
      // now says which one it has.
      console.error('[Dashboard] claims query failed:', err);
      setReadFailed((err && (err.code || err.message)) || 'the claims read was refused');
      setLoading(false);
    });
    return unsubscribe;
  }, [user?.uid, user?.role, user?.committee, user?.isAdmin, (user?.scopes || []).join('|')]);

  const total = claims.length;
  // An archived claim is retired and a draft is not filed yet - neither is OPEN. This counted
  // everything except 'closure', so one archived claim made Open Claims read 12 while the
  // pipeline beneath it summed to 11. Two numbers on one screen that could never agree.
  const urgent = claims.filter(c => c.priority === 'urgent').length;
  // R-6b. ELEVATION_STAGE and its label both come from the ladder - the filter this card sends
  // the Claims page to is the ladder's own label, so the two cannot drift into a filter that
  // matches nothing and a card that looks broken.
  const isGC = user?.role === 'general_chair' || user?.isAdmin;
  const isOfficer = isGC || user?.role === 'local_chair';
  const elevated = claims.filter(c => c.status === ELEVATION_STAGE).length;
  const recent = claims.slice(0, 5);

  // ZONE 1 and ZONE 2, both computed in one pure module and both tested
  // (scripts\test-dashboard-focus.mjs). Neither reads anything this screen has not already read.
  const focus = needsYouNow({
    user, claims, drafts, gcCases, readFailed,
    elevationStage: ELEVATION_STAGE, elevationLabel: ELEVATION_LABEL,
  });
  const counts = workCounts({
    user, claims, readFailed,
    elevationStage: ELEVATION_STAGE, elevationLabel: ELEVATION_LABEL,
  });
  const focusShown = focus.items.slice(0, 6);
  const focusRest = focus.items.length - focusShown.length;
  // ruling 306 item 2: the rights card is for a user who has not been here before. It shows on the
  // landing while there is nothing of his own on the screen, and collapses into More afterwards.
  const rightsOnLanding = !readFailed && !loading && total === 0;

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="topbar-title">Dashboard</h1>
          <div className="topbar-meta">
            Welcome back, {user.name} · {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-primary" onClick={() => onNavigate('new-claim')}>+ New Claim</button>
          <button className="btn btn-primary" style={{ background: '#27AE60' }} onClick={() => onNavigate('quick-file')}>⚡ Quick File</button>
        </div>
      </div>

      <div className="page-content">
        {/* ================= ZONE 1 - ACT. The only thing above the fold. ================= */}
        <div className="card" style={{ marginBottom: 14, borderColor: focus.state === 'items' ? '#8A5A12' : undefined }}>
          <div className="card-header">
            <div>
              <div className="card-title">Needs you now</div>
              <div className="card-subtitle">
                {isOfficer ? 'Time-critical work in your office, soonest first' : 'Your claims with a clock running, soonest first'}
              </div>
            </div>
            {focus.state === 'items' && (
              <span style={{ color: '#E8A020', fontWeight: 700, fontSize: 12 }}>
                {focus.items.length} item{focus.items.length === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#8AA4BF' }}>Reading your claims...</div>
          ) : focus.state === 'refused' ? (
            /* A1 again, and it is the whole reason zone 1 exists in a module with its own tests:
               a refused read must never render as an all-clear. "Nothing needs you today" over a
               query that was denied is the same lie as "0 claims" over a query that was denied. */
            <div className="alert-banner" style={{ margin: 0 }}>
              &#9888; <b>This list could not be built.</b> {focus.message}
            </div>
          ) : focus.state === 'clear' ? (
            <div style={{ padding: '22px 16px', color: '#8AA4BF', fontSize: 14 }}>
              {focus.message}{' '}
              <span style={{ color: '#4A6280', fontSize: 12 }}>
                (nothing is past due and nothing runs out in the next 7 days)
              </span>
            </div>
          ) : (
            <div>
              {focusShown.map(item => {
                const sev = SEV[item.severity] || SEV.waiting;
                return (
                  /* PASS 2 (2026-10-25): a <button>, not a clickable <div>. I wrote these rows as divs
                     this afternoon and the scan - once it could read across lines - showed them as two
                     of the eight controls on this screen that a keyboard cannot reach. A button gets
                     Enter and Space, focus and a name, for free; nothing had to be invented. */
                  <button key={item.key} type="button"
                    onClick={() => onNavigate(item.page, item.arg === null ? undefined : item.arg)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      padding: '10px 14px', marginBottom: 6, cursor: 'pointer', width: '100%',
                      textAlign: 'left', font: 'inherit',
                      background: sev.back, border: '1px solid ' + sev.border, borderRadius: 6,
                    }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', color: sev.colour, fontWeight: 700, fontSize: 13 }}>
                        {sev.mark} {item.text}
                      </span>
                      {item.sub ? (
                        <span style={{ display: 'block', color: '#8AA4BF', fontSize: 11, marginTop: 2 }}>{item.sub}</span>
                      ) : null}
                    </span>
                    <span style={{ color: '#8AA4BF', fontSize: 11, whiteSpace: 'nowrap' }}>open &rarr;</span>
                  </button>
                );
              })}
              {focusRest > 0 && (
                <button type="button"
                     style={{ padding: '6px 14px', color: '#8AA4BF', fontSize: 12, cursor: 'pointer',
                              background: 'transparent', border: 'none', font: 'inherit', textAlign: 'left' }}
                     onClick={() => onNavigate('claims')}>
                  and {focusRest} more &mdash; open Claims
                </button>
              )}
            </div>
          )}
        </div>

        {/* ================= ZONE 2 - WORK. One strip of counts, each a door. ================= */}
        {readFailed ? (
          /* A1: the numbers here are computed from `claims`. If the read was refused, every one of
             them is a zero that means "we do not know", displayed as though it meant "none". They
             are not shown at all in that state - a blank where a number should be sends a man to
             ask; a confident 0 does not. */
          <div className="alert-banner" style={{ marginBottom: 14 }}>
            &#9888; <b>These figures could not be read.</b> The claims query was refused
            ({String(readFailed)}), so this screen does not know how many claims you have or what they
            are worth &mdash; and it will not show you a zero that looks like an answer. Reload the
            page; if it happens again, tell your General Chairperson before you rely on anything here.
          </div>
        ) : (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14,
          }}>
            {counts.map(c => (
              <button key={c.key} type="button"
                onClick={() => onNavigate(c.page, c.arg)}
                style={{
                  flex: '1 1 150px', cursor: 'pointer', padding: '10px 14px', textAlign: 'left', font: 'inherit',
                  background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 6,
                }}>
                <span style={{ display: 'block', color: '#F4F6F8', fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>{c.value}</span>
                <span style={{ display: 'block', color: '#8AA4BF', fontSize: 11, marginTop: 2 }}>{c.label}</span>
              </button>
            ))}
            {isGC && (
              /* T5 / D-18. Kept beside the counts because only the GC can act on it, and '?' when
                 the read did not answer - never a zero. */
              <button type="button" onClick={() => onNavigate('committee')}
                style={{
                  flex: '1 1 150px', cursor: 'pointer', padding: '10px 14px', textAlign: 'left', font: 'inherit',
                  background: '#0D1B2A', border: '1px solid ' + (gcCases ? '#8A5A12' : '#2E4A6B'), borderRadius: 6,
                }}>
                <span style={{ display: 'block', color: gcCases ? '#E8A020' : '#F4F6F8', fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>
                  {gcCases === null ? '?' : gcCases}
                </span>
                <span style={{ display: 'block', color: '#8AA4BF', fontSize: 11, marginTop: 2 }}>
                  {gcCases === null
                    ? 'Discipline forwarded to you - could not be counted'
                    : 'Discipline forwarded to you'}
                </span>
              </button>
            )}
          </div>
        )}

        {/* ruling 306 item 2: the member's rights, on the landing only while he has nothing else there */}
        {rightsOnLanding && <RightsWelcomeCard user={user} onNavigate={onNavigate} />}

        {/* ================= Recent claims - the one table that stays ================= */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Recent Claims</div>
              <div className="card-subtitle">{user?.isAdmin ? 'Most recently filed claims across all committees' : user?.role === 'general_chair' ? `Most recently filed claims in ${user?.committee || 'your committee'}` : user?.role === 'local_chair' ? 'Most recently filed claims in your territory' : 'Your most recently filed claims'}</div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => onNavigate('claims')}>View All</button>
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#8AA4BF' }}>Loading claims...</div>
          ) : readFailed ? (
            /* A1, and this was the worst of the three. "No claims filed yet - File the first claim"
               told a member who HAS claims that he has none, and invited him to file the same claim
               a second time. A duplicate filed against a live window is a real harm, not a cosmetic
               one. It says what actually happened now. */
            <div style={{ padding: 40, textAlign: 'center', color: '#E8A020' }}>
              &#9888; Your claims could not be read &mdash; this is <b>not</b> an empty list.<br />
              <span style={{ color: '#8AA4BF', fontSize: 12 }}>
                Do not file again from here: if you already have a claim, filing a second one does not
                replace it. Reload, and tell your General Chairperson if it happens twice.
              </span>
            </div>
          ) : recent.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#4A6280' }}>No claims filed yet. <button type="button" style={{ color: '#E8A020', cursor: 'pointer', background: 'transparent', border: 'none', font: 'inherit', textDecoration: 'underline' }} onClick={() => onNavigate('new-claim')}>File the first claim.</button></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Claim #</th>
                    <th>Claimant</th>
                    <th>Type</th>
                    <th>Committee</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Amount</th>
                    <th>Occurred</th>
                  </tr>
                </thead>
                <tbody>
                  {/* PASS 2 ROW CONTROL (2026-10-25): the row no longer takes the click. A <tr> is not
                      a control and a <button> cannot wrap cells, so the claim number - which is what a reader
                      aims at anyway - is the control: focusable, announced, operable with Enter. (The comment
                      sat INSIDE the map's return expression at first and broke the build - a JSX arrow may
                      return one element, not an element and a comment.) */}
                  {recent.map(claim => (
                    <tr key={claim.docId}>
                      <td>
                        <button type="button"
                          onClick={() => onNavigate('claim-detail', claim.docId)}
                          style={{ color: '#E8A020', fontWeight: 600, fontFamily: 'monospace',
                                   background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                                   textDecoration: 'underline', fontSize: 'inherit' }}>
                          {claim.id}<span className="sr-only"> - open this claim</span>
                        </button>
                      </td>
                      <td>{claim.claimantName}</td>
                      <td>{claimBasisLabel(claim) || claim.claimType || <span style={{ color: '#4A6280' }}>-</span>}</td>
                      <td><span style={{ fontSize: 11, color: '#8AA4BF' }}>{claimCommittee(claim)}</span></td>
                      <td><span className={`badge badge-${claim.status}`}>{claim.status}</span></td>
                      <td><span className={`badge badge-${claim.priority}`}>{claim.priority}</span></td>
                      <td style={{ fontWeight: 600 }}>${parseFloat(claim.amountClaimed || 0).toLocaleString()}</td>
                      <td style={{ color: '#8AA4BF' }}>{claim.incidentDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ================= ZONE 3 - MORE. Everything that used to shout. ================= */}
        <button
          onClick={() => setShowMore(v => !v)}
          style={{
            width: '100%', marginTop: 14, padding: '10px 14px', cursor: 'pointer',
            background: 'transparent', border: '1px solid #2E4A6B', borderRadius: 6,
            color: '#A8C4DF', fontSize: 12, fontWeight: 700, textAlign: 'left',
          }}>
          {showMore ? '▾' : '▸'} {showMore ? 'Hide' : 'Show'} notifications, handovers, tally, contact details and the claim pipeline
        </button>

        {showMore && (
          <div style={{ marginTop: 12 }}>
            {/* Return visitor strip - the Quick File button in the top bar goes to the same place,
                so it is reference here rather than a second banner on the landing. */}
            {showReturnBanner && profile && (
              <div style={{
                background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 6,
                padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexWrap: 'wrap', gap: 8
              }}>
                <div style={{ fontSize: 12, color: '#A8C4DF' }}>
                  <span style={{ color: '#E8A020', fontWeight: 700 }}>Your profile is on file &mdash;</span>{' '}
                  <span style={{ color: '#F4F6F8' }}>{profile.railroad}</span>
                  {profile.workLocation && <span style={{ color: '#8AA4BF' }}> · {profile.workLocation}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => onNavigate('quick-file')}
                    style={{ background: '#27AE60', border: 'none', borderRadius: 4, color: 'white', fontSize: 11, fontWeight: 700, padding: '4px 14px', cursor: 'pointer' }}>
                    ⚡ Quick File with saved info
                  </button>
                  <button onClick={() => setShowReturnBanner(false)}
                    style={{ background: 'transparent', border: '1px solid #2E4A6B', borderRadius: 4, color: '#8AA4BF', fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Notifications - 6a-10. Unread first; the only write is readAt on your own. */}
            <NotificationCentre user={user} onViewClaim={onViewClaim} compact />
            <HandoverInbox user={user} />
            <OfficeCopyPanel user={user} />
            <ClaimTally user={user} mode="self" onNavigate={onNavigate} />
            {!rightsOnLanding && <RightsWelcomeCard user={user} onNavigate={onNavigate} />}   {/* ruling 306 item 2 */}
            <ContactSection user={user} />   {/* Record 610 (ruling 259) */}

            {/* A1: every stage count comes from `claims`; on a refused read the whole ladder reads
                zero, which is a picture of a member with nothing in flight. Not shown rather than
                shown wrong. */}
            {!readFailed && (
            <div className="pipeline">
              {PIPELINE_STAGES.map((stage, i) => {
                const count = claims.filter(c => c.status === stage.key).length;
                return (
                  <button key={i} type="button" className="pipeline-stage"
                    onClick={() => onNavigate('claims')}
                    style={{
                      cursor: count > 0 ? 'pointer' : 'default',
                      border: count > 0 ? '1px solid #E8A020' : '1px solid transparent',
                      borderRadius: 6,
                      padding: '4px',
                      transition: 'all 0.2s',
                      position: 'relative',
                      background: 'transparent',
                      font: 'inherit',
                    }}
                    aria-label={count + ' ' + stage.label + ' claim' + (count !== 1 ? 's' : '') + ' - open Claims'}
                  >
                    <span className="pipeline-stage-count" style={{ display: 'block', color: count > 0 ? '#E8A020' : '#4A6280' }}>{count}</span>
                    <span className="pipeline-stage-name" style={{ display: 'block', color: count > 0 ? '#F4F6F8' : '#4A6280' }}>{stage.label}</span>
                    {count > 0 && (
                      <span style={{ display: 'block', fontSize: 9, color: '#8AA4BF', textAlign: 'center', marginTop: 2 }}>↓ view</span>
                    )}
                  </button>
                );
              })}
            </div>
            )}

            {/* The counts zone 2 shows in one line, kept here in full for anyone who wants them all. */}
            {!readFailed && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{total}</div>
                  <div className="stat-label">Total Claims</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{claims.filter(c => !NOT_OPEN.includes(c.status)).length}</div>
                  <div className="stat-label">Open Claims</div>
                </div>
                <div className="stat-card alert">
                  <div className="stat-value">{urgent}</div>
                  <div className="stat-label">Urgent Priority</div>
                </div>
                {isGC && (
                  <button type="button" className="stat-card"
                       style={{ cursor: elevated > 0 ? 'pointer' : 'default', font: 'inherit', textAlign: 'left',
                                border: elevated > 0 ? '1px solid #E8A020' : '1px solid transparent' }}
                       onClick={() => elevated > 0 && onNavigate('claims', { filterStatus: ELEVATION_LABEL })}>
                    <span className="stat-value" style={{ display: 'block', color: elevated > 0 ? '#E8A020' : undefined }}>{elevated}</span>
                    <span className="stat-label" style={{ display: 'block' }}>
                      {elevated > 0 ? 'Sent up to you' : 'Sent up to you - nothing yet'}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
